import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin } from '../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { SettingsRegistryTable } from '../../../components/admin/SettingsRegistryTable'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'

export const Route = createFileRoute('/admin/settings/')({
  component: RouteComponent,
})

// Gated with useIsPlatformAdmin: this route lives outside any $org context
// (a cross-org, server-wide surface — same placement rationale as
// /organizations), so there is no org-membership check to piggy-back on.
// Auth resolution is async (AuthContext.loading), so the guard renders
// nothing until it resolves and only then redirects a non-admin — this
// avoids a flash of the registry table before the check completes.
function RouteComponent() {
  const { t } = useTranslation('admin')
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  // This route is deliberately org-less (see the placement rationale above),
  // so there is no `$org` route param to build the "Home" / "Settings"
  // crumb links from. Fall back to the signed-in user's org, same as
  // AppSidebar.tsx and CommandPalette.tsx do for other org-less contexts.
  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('registry.title') },
  ])

  if (loading || !isPlatformAdmin) {
    return null
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <PageHeader
        icon={SECTION_ICONS.adminSettings}
        title={t('registry.title')}
        description={t('registry.subtitle')}
      />

      <SettingsRegistryTable />
    </div>
  )
}
